/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import {
  Accordion,
  AccordionHeader,
  AccordionPanel,
  Button,
  Dialog,
  Divider,
  Radio,
  RadioGroup,
  Typography2,
  breakpoints,
} from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import CheckableExpandableSearchFilterGroup, {
  type UnappliedFiltersByFacet,
} from 'bundles/search-common/components/experiments/filters2024/CheckableExpandableSearchFilterGroup';
import { filterValueAdapter } from 'bundles/search-common/components/experiments/filters2024/filterValueAdapter';
import { getFiltersByFacet } from 'bundles/search-common/components/experiments/filters2024/utils';
import { useSearchFilters } from 'bundles/search-common/components/filters/useSearchFilters';
import { DEFAULT_SORTED_VALUE, NEW_SORTED_VALUE, SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { DEFAULT_SEARCH_FILTERS_ALL_FILTERS_PANEL } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchPageEventingData, SearchSortType } from 'bundles/search-common/types';
import { scrollUpIfNecessary } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

const getCustomFilterGroupLabel = (): Record<string, string> => ({
  productTypeDescription: _t('Learning Product'),
  productDifficultyLevel: _t('Level'),
  topic: _t('Topic'),
});

type Props = {
  isOpen: boolean;
  onClose: () => void;
  eventingData: SearchPageEventingData;
};

const styles = {
  panelContainer: css`
    .cds-Dialog-dialog {
      max-width: 360px;
      width: 360px;
      height: 100vh;
      min-height: 100vh;
      position: fixed;
      right: 0;
      top: 0;
      left: auto;
      border-radius: 0;
      transform: none;

      ${breakpoints.down('sm')} {
        max-width: 100%;
        width: 100%;
        left: 0;
        right: 0;
      }
    }

    /* Position close button with 24px top/bottom and 16px from right */
    .cds-iconButton-ghost {
      position: absolute;
      top: var(--cds-spacing-200);
      right: var(--cds-spacing-200);
      bottom: auto;
      left: auto;
      color: var(--cds-color-grey-975);

      ${breakpoints.down('sm')} {
        top: var(--cds-spacing-50);
      }
    }
  `,
  dialogTitle: css`
    position: absolute;
    top: var(--cds-spacing-300);
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;

    ${breakpoints.down('sm')} {
      top: 10px;
    }
  `,
  divider: css`
    position: absolute;
    top: 72px;
    left: 0;
    right: 0;

    ${breakpoints.down('sm')} {
      top: 44px;
    }
  `,
  content: css`
    position: absolute;
    top: 72px; /* Below title and divider */
    bottom: 104px; /* Above footer including divider */
    left: 0;
    right: 0;
    padding: 0;
    overflow-y: auto;

    ${breakpoints.down('sm')} {
      top: 44px;
      bottom: 76px;
    }
  `,
  accordionHeaderButton: css`
    .cds-AccordionHeader-button {
      padding: var(--cds-spacing-150) var(--cds-spacing-200) var(--cds-spacing-150) var(--cds-spacing-200);
      margin: 0;
    }

    .cds-AccordionHeader-content {
      margin: 0;
    }
  `,
  accordionHeaderLabel: css`
    .cds-AccordionHeader-labelGroup {
      span {
        font-weight: 400;
        font-family: var(--cds-typography-body-primary);
        line-height: 1.5rem;
        letter-spacing: var(--cds-letter-spacing-0);
      }
    }
  `,

  sortBySection: css`
    margin-top: var(--cds-spacing-150);
  `,
  footer: css`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    background-color: var(--cds-color-white-0);
  `,
  footerButtons: css`
    display: flex;
    gap: var(--cds-spacing-100);
    padding: var(--cds-spacing-100) var(--cds-spacing-200) var(--cds-spacing-200) var(--cds-spacing-200);

    ${breakpoints.up('sm')} {
      flex-direction: column;
    }

    ${breakpoints.down('sm')} {
      flex-direction: row;
      padding-bottom: var(--cds-spacing-400);
    }
  `,
};

const AllSearchFiltersPanel = (props: Props) => {
  const { isOpen, onClose, eventingData } = props;
  const { filterGroupConfigs, filterKeyToOptionsMap, facetFilters } = useSearchFilters({
    facetOrder: DEFAULT_SEARCH_FILTERS_ALL_FILTERS_PANEL,
  });
  const { applySearchStateUpdates, getSearchResults } = useSearch();
  const { sortBy } = getSearchResults(SEARCH_MAIN_INDEX_ID) || {};
  const [unappliedFilters, setUnappliedFilters] = useState<UnappliedFiltersByFacet>({});
  const [selectedSortByValue, setSelectedSortByValue] = useState<SearchSortType>(sortBy || DEFAULT_SORTED_VALUE);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    sortBy: true, // Start with sort section expanded
  });

  const track = useTracker();

  // This is to initially update the state with any filters that have already been selected
  useEffect(() => {
    if (facetFilters && facetFilters.length > 0) {
      setUnappliedFilters(getFiltersByFacet(facetFilters));
    }
  }, [facetFilters]);

  // This is to update the state with any changes to the sort that happen outside of the panel
  useEffect(() => {
    if (sortBy) {
      setSelectedSortByValue(sortBy);
    }
  }, [sortBy]);

  const handleFilterChange = (newFilters: UnappliedFiltersByFacet) => {
    setUnappliedFilters((prevUnappliedFilters) => ({ ...prevUnappliedFilters, ...newFilters }));
  };

  const handleSortByChange = (sortValue: SearchSortType) => {
    setSelectedSortByValue(sortValue);
  };

  const handleAccordionChange = (sectionKey: string) => (event: React.ChangeEvent<unknown>, expanded: boolean) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: expanded,
    }));
  };

  const hasFiltersApplied = Object.keys(unappliedFilters).some(
    (key) => unappliedFilters[key] && unappliedFilters[key].length > 0
  );

  const handleClearAll = () => {
    track('apply_filter', {
      filter: [],
      filterSource: 'unified_modal',
      isReset: true,
    });

    setSelectedSortByValue(DEFAULT_SORTED_VALUE);
    setUnappliedFilters({});
  };

  const handleApply = () => {
    applySearchStateUpdates(SEARCH_MAIN_INDEX_ID, {
      sortBy: selectedSortByValue,
      clearFilters: true,
      addFilters: Object.values(unappliedFilters).flat(),
    });
    onClose();
    scrollUpIfNecessary({ mustBeScrolledPastThisElementId: 'search-results-header-wrapper' });

    // prevent event from firing, when there are no filters (i.e is clicked after a reset)
    if (Object.keys(unappliedFilters).length) {
      track('apply_filter', {
        filter: Object.values(unappliedFilters).flat(),
        filterSource: 'unified_modal',
      });
    }
  };

  const handleClose = () => {
    onClose();
    setUnappliedFilters(facetFilters && facetFilters.length > 0 ? getFiltersByFacet(facetFilters) : {});
    setSelectedSortByValue(sortBy || DEFAULT_SORTED_VALUE);
  };

  return (
    <Dialog css={styles.panelContainer} open={isOpen} onClose={handleClose} aria-labelledby="dialogHeading">
      <Typography2 variant="subtitleLarge" component="h3" css={styles.dialogTitle} id="dialogHeading">
        {_t('Filter & Sort')}
      </Typography2>
      <Divider css={styles.divider} />

      <div css={styles.content}>
        {/* Sort By Section */}
        <div css={styles.sortBySection}>
          <Accordion variant="silent" expanded={expandedSections.sortBy} onChange={handleAccordionChange('sortBy')}>
            <AccordionHeader
              label={_t('Sort by')}
              indicatorAlignment="end"
              css={[styles.accordionHeaderButton, !expandedSections.sortBy && styles.accordionHeaderLabel]}
            />
            <AccordionPanel>
              <RadioGroup
                value={selectedSortByValue}
                onChange={(e) => handleSortByChange(e.target.value as SearchSortType)}
                aria-label={_t('Sort by')}
              >
                <Radio value={DEFAULT_SORTED_VALUE} label={_t('Best Match')} />
                <Radio value={NEW_SORTED_VALUE} label={_t('Newest')} />
              </RadioGroup>
            </AccordionPanel>
          </Accordion>
        </div>

        {/* Filter Sections */}
        {filterGroupConfigs
          .filter((group) => !!filterKeyToOptionsMap[group.property]?.length)
          .map((group) => (
            <div key={group.property}>
              <Accordion
                variant="silent"
                expanded={expandedSections[group.property]}
                onChange={handleAccordionChange(group.property)}
              >
                <AccordionHeader
                  label={getCustomFilterGroupLabel()[group.property] || group.getName()}
                  indicatorAlignment="end"
                  css={[styles.accordionHeaderButton, !expandedSections[group.property] && styles.accordionHeaderLabel]}
                />
                <AccordionPanel>
                  <CheckableExpandableSearchFilterGroup
                    groupConfig={group}
                    eventingData={{
                      searchIndex: eventingData.searchIndex,
                      searchIndexPosition: eventingData.searchIndexPosition,
                    }}
                    shouldHideGroupLabel={true}
                    {...filterValueAdapter(
                      group.property,
                      filterKeyToOptionsMap[group.property],
                      unappliedFilters[group.property],
                      handleFilterChange
                    )}
                  />
                </AccordionPanel>
              </Accordion>
            </div>
          ))}
      </div>

      <div css={styles.footer}>
        <Divider />
        <div css={styles.footerButtons}>
          <Button fullWidth variant="secondary" size="small" onClick={handleClearAll} disabled={!hasFiltersApplied}>
            {_t('Clear all')}
          </Button>
          <Button fullWidth variant="primary" size="small" onClick={handleApply}>
            {_t('Apply')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default AllSearchFiltersPanel;
